import { createFileRoute, Link } from '@tanstack/react-router';
import { useJobs, useQuota } from '@/lib/api/queries';
import { isActive, JOB_STATUS_LABEL } from '@/lib/api/types';
import { formatInt } from '@/lib/format';

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-card bg-surface p-5 shadow-card">
      <p className="text-xs font-medium uppercase tracking-wide text-secondary">{label}</p>
      <p className="mt-2 text-3xl font-extrabold tabular-nums">{value}</p>
      {hint ? <p className="mt-1 text-xs text-tertiary">{hint}</p> : null}
    </div>
  );
}

function Dashboard() {
  const { data: quota } = useQuota();
  const { data: jobs } = useJobs();

  const running = jobs?.filter((job) => isActive(job.status)) ?? [];
  const leads = jobs?.reduce((sum, job) => sum + job.places_new, 0) ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">Tableau de bord</h1>
        <p className="text-sm text-secondary">Vue d'ensemble de vos extractions.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Runs actifs" value={formatInt(running.length)} />
        <Stat label="Leads collectés" value={formatInt(leads)} hint="toutes extractions confondues" />
        <Stat
          label="Quota restant"
          value={quota ? formatInt(quota.remaining) : '—'}
          hint={quota ? `sur ${formatInt(quota.limit)} ce mois-ci` : undefined}
        />
        <Stat label="Runs au total" value={formatInt(jobs?.length ?? 0)} />
      </div>

      <div className="rounded-card bg-surface p-5 shadow-card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-bold">Derniers runs</h2>
          <Link to="/new" className="rounded-control bg-accent px-3 py-1.5 text-sm font-semibold text-white">
            Nouveau run
          </Link>
        </div>
        {jobs && jobs.length > 0 ? (
          <ul className="flex flex-col divide-y divide-hairline">
            {jobs.slice(0, 6).map((job) => (
              <li key={job.id}>
                <Link
                  to="/runs/$runId"
                  params={{ runId: job.id }}
                  className="flex items-center justify-between py-3 text-sm hover:text-accent"
                >
                  <span className="truncate font-medium">{job.name}</span>
                  <span className="ml-4 shrink-0 tabular-nums text-secondary">
                    {JOB_STATUS_LABEL[job.status]} · {formatInt(job.tasks_done)}/
                    {formatInt(job.tasks_total)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-6 text-center text-sm text-secondary">
            Aucun run pour l'instant. Lancez votre première extraction.
          </p>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/')({ component: Dashboard });
